/* eslint-disable react/prop-types */
import {
    forwardRef,
    Stack,
    Tag,
    TagLabel,
    Wrap,
    WrapItem,
    Text,
    Heading,
    Box,
    Grid,
    GridItem,
    Container,
    HStack,
    Spacer,
    Flex,
    Link,
} from '@chakra-ui/react'
import { isFunction } from '@chakra-ui/utils'
import { format, parseISO } from 'date-fns'
import { Fragment, useMemo } from 'react'
import { getIncomeDisplay } from 'shared/utils/incomeDisplay'
import { getDisplayOutsourcingFlag } from 'shared/utils/outsourcingFlag'

// const numOfPeople = {
//     dd: {
//         _after: {
//             content: '"人"',
//         },
//     },
// }
// const numOfBeds = {
//     dd: {
//         _after: {
//             content: '"床"',
//         },
//     },
// }

/**
 * 郵便番号を 123-4567 形式に整形
 *
 * @param {string | undefined} value
 * @returns {string | undefined}
 */
const formatPostalCode = (value) => {
    const digits = String(value).replace(/[^0-9]/g, '')
    if (digits.length !== 7) {
        return value
    }
    return digits.slice(0, 3) + '-' + digits.slice(3)
}

const Section = forwardRef((props, ref) => {
    return (
        <Stack
            ref={ref}
            as="section"
            borderBottomWidth="1px"
            pb={4}
            mb={4}
            {...props}
        />
    )
})

export const ProposalContent = forwardRef(({ data, ...props }, ref) => {
    return (
        <Container
            ref={ref}
            maxW="container.lg"
            px={4}
            {...props}
            // これはpropsの上書き対象外
            sx={{
                '&.mode-pc,&.mode-sp': {
                    '.confidential': {
                        display: 'none !important',
                    },
                },
                dl: {
                    gap: 2,
                    gridTemplateColumns: 'auto',
                },
                '.card-container': {
                    p: '16px',
                    '.card': {
                        p: '8px',
                    },
                },
                '&:not(.mode-sp)': {
                    '@media (min-width: 30em)': {
                        dl: {
                            gridTemplateColumns: 'auto minmax(0, 1fr)',

                            'dt .flex-label': {
                                flex: 1,
                            },
                        },
                        '.card-container': {
                            p: '32px',
                            '.card': {
                                p: '32px',
                            },
                        },
                    },
                },
            }}
        >
            <Box
                className="confidential"
                p={1}
                borderWidth="1px"
                display="inline-block"
                fontSize="xs"
                sx={{
                    '@media (min-width: 48em)': {
                        position: 'absolute',
                        top: '5px',
                        right: '5px',
                    },
                }}
            >
                本御提案書は非公開情報を含みますので、外部への公開はお控えください。
            </Box>
            <HStack mt="32px" mb={2}>
                <Heading fontSize="18px">求人情報ご提案書</Heading>
                <Spacer />
                <Text
                    size="sm"
                    fontWeight="bold"
                    color="tryt.gray-4"
                    fontSize="sm"
                >
                    <DateText>{data.updatedAt}</DateText>更新
                </Text>
            </HStack>

            <Box className="card-container" bg="tryt.pale-1">
                <Box className="card" bg="white" rounded="16px">
                    <Section>
                        <Wrap>
                            {data.tags.map((tag) => (
                                <RecommendedPoint key={tag}>
                                    {tag}
                                </RecommendedPoint>
                            ))}
                        </Wrap>
                        <Heading fontSize="18px" mt={3}>
                            {data.facility.name}
                        </Heading>
                        <Box sx={{ 'span + span': { ml: 2 } }}>
                            <Conditional isShow={data.facility.postalCode}>
                                <Text as="span" display="inline-block">
                                    〒
                                    {formatPostalCode(data.facility.postalCode)}
                                </Text>
                            </Conditional>
                            <Conditional isShow={data.facility.address}>
                                <Text as="span" display="inline-block">
                                    {data.facility.address}
                                </Text>
                            </Conditional>
                        </Box>
                    </Section>
                    <Conditional isShow={data.advisorComment}>
                        <Section>
                            <Heading size="sm">
                                キャリアアドバイザーからのコメント
                            </Heading>
                            <Text whiteSpace="pre-line">
                                {data.advisorComment}
                            </Text>
                        </Section>
                    </Conditional>

                    <Section borderBottomWidth="0px">
                        <Heading size="sm">求人情報</Heading>
                        <OfferInformation offer={data.offer} />
                    </Section>
                    <Section>
                        <Heading size="sm">
                            事業者情報
                            <Conditional isShow={data.facility.modifiedDate}>
                                <Text
                                    as="span"
                                    fontSize="sm"
                                    fontWeight="normal"
                                >
                                    （
                                    <DateText>
                                        {data.facility.modifiedDate}
                                    </DateText>
                                    時点）
                                </Text>
                            </Conditional>
                        </Heading>
                        <Grid as="dl" className="facility-info">
                            <Data title="運営法人">
                                {data.facility.corporation?.name}
                            </Data>
                            <Data title="運営方針">
                                {data.facility.officeDirection}
                            </Data>

                            <Data
                                title="事業所開始年月日"
                                isShow={data.facility.officeStartDate}
                            >
                                {() => (
                                    <DateText>
                                        {data.facility.officeStartDate}
                                    </DateText>
                                )}
                            </Data>
                            <Data
                                title="事業所URL"
                                isShow={data.facility.officeUrl}
                            >
                                {() => (
                                    <Link
                                        isExternal
                                        href={data.facility.officeUrl}
                                    >
                                        {data.facility.officeUrl}
                                    </Link>
                                )}
                            </Data>
                            <Data title="施設区分">
                                {data.facility.serviceType}
                            </Data>
                            <Data title="利用定員">
                                {data.facility.officeCapacity}
                            </Data>
                            <Data title="総従業員数">
                                {data.facility.officeEmployeeCount}
                            </Data>
                            <BusinessHours
                                title="営業時間"
                                data={data.facility}
                            />
                            <Data title="診療科目">
                                {data.facility.clinicalDepartment}
                            </Data>
                            <Data title="総病床数">
                                {data.facility.totalNumberOfBeds}
                            </Data>
                            <Data
                                title="取引先区分"
                                isShow={data.facility.outsourceflg}
                            >
                                {() =>
                                    getDisplayOutsourcingFlag(
                                        data.facility.outsourceflg
                                    )
                                }
                            </Data>
                            <Data title="休業日">
                                {data.facility.closedDay}
                            </Data>
                        </Grid>
                    </Section>
                </Box>
            </Box>
        </Container>
    )
})

const NoInformation = () => {
    return (
        <Box p={4} borderWidth="1px">
            <Text>
                この求人は募集の確認が取れていませんが、希望条件にマッチする可能性があるため担当CAが法人に求人獲得の交渉中です。
            </Text>
            <Text>
                もしご興味がある場合は他の案件よりも優先度を上げて交渉にあたりますので、以下のボタンから「応募したい」もしくは「キープしたい」を押下してください。
            </Text>
            <Text>
                ご質問がある場合は、担当CAまで直接お問い合わせください。
            </Text>
        </Box>
    )
}

const OfferInformation = ({ offer }) => {
    const isEmpty = (obj) =>
        obj === undefined ||
        obj === null ||
        obj === '' ||
        (Array.isArray(obj) && obj.length === 0)
    const hasSomeField = (offer) => {
        const fields = [
            'qualification',
            'employmentType',
            'workShift',
            'incomeDisplayClass',
            'bonus',
            'allowance',
            'workingHours',
            'holiday',
            'socoialInsurance',
            'welfare',
            'hasBabyFarm',
            'carCommuting',
        ]
        return fields.some((field) => !isEmpty(offer[field]))
    }

    if (isEmpty(offer) || !hasSomeField(offer)) {
        return <NoInformation />
    }
    return (
        <Grid as="dl" p={4} className="offer-info" borderWidth="1px">
            <Data title="応募職種">{offer.qualification}</Data>
            <Data title="雇用形態">{offer.employmentType}</Data>
            <Data title="勤務形態">{offer.workShift}</Data>
            <Data
                title={`固定給(${offer?.incomeDisplayClass})`}
                isShow={offer.incomeDisplayClass}
            >
                {() => getIncomeDisplay(offer)}
            </Data>
            <Data title="賞与">{offer.bonus}</Data>
            <Data title="諸手当">{offer.allowance}</Data>
            <Data title="勤務時間">
                {offer?.workingHours?.map(({ from, to, rest }, index) => {
                    return (
                        // NOTE: 保存前はidないのでしゃーなし
                        <Box key={index}>
                            <Text as="span">{from}</Text>
                            <Conditional isShow={to}>
                                ～<Text as="span">{to}</Text>
                            </Conditional>
                            <Conditional isShow={rest}>
                                <Text as="span" ml="1em">
                                    休憩{rest}分
                                </Text>
                            </Conditional>
                        </Box>
                    )
                })}
            </Data>
            <Data title="休日・休暇">{offer.holiday}</Data>
            <Data title="社会保険">{offer.socoialInsurance}</Data>
            <Data title="福利厚生">{offer.welfare}</Data>
            <Data title="託児所">{offer.hasBabyFarm}</Data>
            <Data title="車通勤">{offer.carCommuting}</Data>
        </Grid>
    )
}

const RecommendedPoint = forwardRef(({ children, ...props }, ref) => {
    return (
        <WrapItem ref={ref} {...props}>
            <Tag size="lg">
                <TagLabel noOfLines={1}>{children}</TagLabel>
            </Tag>
        </WrapItem>
    )
})

const Data = forwardRef(({ title, children, isShow, ...props }, ref) => {
    if (isFunction(children)) {
        return (
            <Conditional isShow={isShow}>
                <Box ref={ref} display="contents" {...props}>
                    <Label>{title}</Label>
                    <Value>{children()}</Value>
                </Box>
            </Conditional>
        )
    }
    return (
        <Conditional isShow={!!children?.length}>
            <Box ref={ref} display="contents" {...props}>
                <Label>{title}</Label>
                <Value>{children}</Value>
            </Box>
        </Conditional>
    )
})

const Label = forwardRef(({ children, ...props }, ref) => {
    return (
        <Flex ref={ref} as="dt" color="tryt.gray-4" {...props}>
            <Text as="span" className="flex-label">
                {children}
            </Text>
            <Text as="span">：</Text>
        </Flex>
    )
})

const Value = forwardRef(({ children, ...props }, ref) => {
    return (
        <GridItem ref={ref} as="dd" whiteSpace="pre-line" {...props}>
            {children}
        </GridItem>
    )
})

const Conditional = ({ isShow, children }) => {
    return isShow && children
}

const DateText = forwardRef(({ children, ...props }, ref) => {
    if (typeof children === 'string') {
        children = parseISO(children)
    }
    return (
        <Text as="span" ref={ref} {...props}>
            {children && format(children, 'yyyy年M月d日')}
        </Text>
    )
})

const BusinessHours = ({ title, data }) => {
    const children = useMemo(() => {
        const elem = []
        if (data.businessHours?.weekday) {
            elem.push(
                <Fragment key="weekday">
                    <GridItem>平日</GridItem>
                    <GridItem>{data.businessHours.weekday}</GridItem>
                </Fragment>
            )
        }
        if (data.businessHours?.saturday) {
            elem.push(
                <Fragment key="saturday">
                    <GridItem>土曜</GridItem>
                    <GridItem>{data.businessHours.saturday}</GridItem>
                </Fragment>
            )
        }
        if (data.businessHours?.sunday) {
            elem.push(
                <Fragment key="sunday">
                    <GridItem>日曜</GridItem>
                    <GridItem>{data.businessHours.sunday}</GridItem>
                </Fragment>
            )
        }
        if (data.businessHours?.holiday) {
            elem.push(
                <Fragment key="holiday">
                    <GridItem>祝日</GridItem>
                    <GridItem>{data.businessHours.holiday}</GridItem>
                </Fragment>
            )
        }
        if (data.regularHoliday) {
            elem.push(
                <Fragment key="regularHoliday">
                    <GridItem>休日</GridItem>
                    <GridItem>{data.regularHoliday}</GridItem>
                </Fragment>
            )
        }
        return elem
    }, [data])
    return (
        <Data isShow={!!children.length} title={title}>
            {() => (
                <Grid
                    templateColumns="max-content 1fr"
                    wordBreak="break-word"
                    overflowWrap="anywhere"
                    columnGap={2}
                >
                    {children}
                </Grid>
            )}
        </Data>
    )
}
